import { supabase } from '@sixam/supabase/client';
import type { Database, Json } from '@sixam/shared/database.types';
import { systemNowMs } from '@sixam/shared/clock';
import {
  normalizeProfileLinks,
  profileLinksToText,
  type ProfileLink,
} from '@sixam/shared/member-profile-core';

export type TenantProfile = Database['public']['Tables']['tenants']['Row'];

export type TenantProfileUpdateInput = Readonly<{
  bio: string | null;
  contactEmail: string | null;
  links: ProfileLink[];
  name: string;
  phone: string | null;
  phoneIsPublic: boolean;
}>;

export { normalizeProfileLinks, profileLinksToText, type ProfileLink };

export async function updateTenantProfile(
  tenantId: string,
  input: TenantProfileUpdateInput
): Promise<TenantProfile> {
  const { data, error } = await supabase
    .from('tenants')
    .update({
      bio: input.bio,
      contact_email: input.contactEmail,
      links: input.links as unknown as Json,
      name: input.name,
      phone: input.phone,
      phone_is_public: input.phoneIsPublic,
    })
    .eq('id', tenantId)
    .select('*')
    .single();

  if (error) {
    throw error;
  }

  return data;
}

export async function uploadTenantAvatar(
  tenantId: string,
  asset: {
    fileName?: string | null;
    mimeType?: string | null;
    uri: string;
  }
): Promise<string> {
  const extension = asset.fileName?.split('.').pop()?.toLowerCase() ?? 'jpg';
  const path = `${tenantId}/avatar-${systemNowMs()}.${extension}`;
  const blob = await fetch(asset.uri).then((response) => response.blob());
  const { error } = await supabase.storage
    .from('tenant-avatars')
    .upload(path, blob, { contentType: asset.mimeType ?? 'image/jpeg', upsert: true });

  if (error) {
    throw error;
  }

  const { data } = supabase.storage.from('tenant-avatars').getPublicUrl(path);
  const publicUrl = data.publicUrl;
  const { error: updateError } = await supabase
    .from('tenants')
    .update({ avatar_url: publicUrl })
    .eq('id', tenantId);

  if (updateError) {
    throw updateError;
  }

  return publicUrl;
}
